import type { EnvelopeStatus, EnvelopeView, SignerStatus, SignerView } from '@argink/shared';
import { Card, StatusBadge } from '@argink/ui';
import type { StatusBadgeTone } from '@argink/ui';
import { DOCUMENT_FORMAT_LABEL, ENVELOPE_STATUS_LABEL, SIGNER_STATUS_LABEL, formatDate, modalityCopyFor } from '../lib/format';

export interface DocumentCardProps {
  envelope: EnvelopeView;
  signer: SignerView;
}

/** `completed` é a única variante de envelope que representa prova/conclusão —
 * as demais são apenas estado operacional (Trust Blue) ou falha (danger). */
function envelopeStatusTone(status: EnvelopeStatus): StatusBadgeTone {
  switch (status) {
    case 'completed':
      return 'proof';
    case 'in_progress':
      return 'info';
    case 'expired':
    case 'cancelled':
    case 'declined':
      return 'danger';
    default:
      return 'neutral';
  }
}

/** `signed` é a única variante de signatário que representa prova/conclusão. */
function signerStatusTone(status: SignerStatus): StatusBadgeTone {
  switch (status) {
    case 'signed':
      return 'proof';
    case 'notified':
    case 'in_session':
      return 'info';
    case 'declined':
    case 'expired':
      return 'danger';
    default:
      return 'neutral';
  }
}

/**
 * Card de metadados do documento/status — desde a D-063 o CONTEÚDO do PDF vive
 * no `PdfViewer` (cerimônia documento-cêntrica, #91); este card vira o resumo
 * compacto da sidebar: nome, formato, status e o hash como âncora de
 * integridade visível (o signatário confere o hash contra o que recebeu por
 * outro canal, se quiser — metadado, nunca substituto do documento).
 *
 * Deixa a modalidade EXPLÍCITA (mandato §5, D-039): "Assinatura Simples —
 * Verificação por e-mail e registro da manifestação" ou "Assinatura
 * Qualificada — Certificado digital ICP-Brasil". Nunca `avancada` como opção
 * equivalente — ver `modalityCopyFor`.
 */
export function DocumentCard({ envelope, signer }: DocumentCardProps) {
  const doc = envelope.documents[0];
  const modality = modalityCopyFor(signer.required_signature_level);

  return (
    <Card aria-labelledby="doc-title">
      <h2 id="doc-title" className="card__title">
        {doc?.name ?? 'Documento sem nome'}
      </h2>

      <p className="modality-line">
        <strong>{modality.title}</strong> — {modality.detail}
      </p>

      {/* §3/§23 do M6-C: a superfície NORMAL responde o que o signatário pergunta —
          em que pé está o envio, em que pé está a assinatura DELE, até quando dá. O
          resto era infraestrutura na cara de quem só recebeu um link: "Status do
          ENVELOPE" (a palavra do contrato, em caixa alta, como rótulo de campo) e
          "Formato: PADES" (jargão que a lista compartilhada barra por nome). Nada
          disso sumiu — desceu para "Detalhes técnicos", que é onde quem investiga
          procura. Achado visual: só apareceu na captura da tela real. */}
      <dl className="kv-grid">
        <div className="kv-grid__item">
          <dt>Situação do envio</dt>
          <dd>
            <StatusBadge tone={envelopeStatusTone(envelope.status)}>
              {ENVELOPE_STATUS_LABEL[envelope.status]}
            </StatusBadge>
          </dd>
        </div>
        <div className="kv-grid__item">
          <dt>Sua assinatura</dt>
          <dd>
            <StatusBadge tone={signerStatusTone(signer.status)}>{SIGNER_STATUS_LABEL[signer.status]}</StatusBadge>
          </dd>
        </div>
        {envelope.expires_at ? (
          <div className="kv-grid__item">
            <dt>Prazo para assinar</dt>
            <dd>{formatDate(envelope.expires_at)}</dd>
          </div>
        ) : null}
      </dl>

      {/* O hash CONTINUA na superfície normal do consentimento (`ConsentCard`), e ali
          é outra coisa: lá ele identifica o documento sobre o qual o aceite recai, é
          parte do termo. Aqui era ficha técnica sem pergunta que a justificasse. */}
      <details className="document-card__technical">
        <summary>Detalhes técnicos</summary>
        <dl className="kv-grid">
          <div className="kv-grid__item">
            <dt>Formato do arquivo assinado</dt>
            {/* M6-C liberou jargão de cripto aqui dentro (jargão é aceito em "Detalhes
                técnicos" por decisão) — mas aceito não é preferível. `doc.format` é
                valor de CONTRATO (`pades`/`cades`/`xades`, o perfil de assinatura, não
                o tipo do arquivo); renderizar cru (`.toUpperCase()`) mostrava "PADES",
                que é o mesmo jargão que M6-A tinha corrigido noutra sessão que nunca
                chegou a `main`. DOCUMENT_FORMAT_LABEL descreve o ARQUIVO que o
                signatário abriu, que é a pergunta que "Detalhes técnicos" responde. */}
            <dd>{doc ? DOCUMENT_FORMAT_LABEL[doc.format] : '—'}</dd>
          </div>
          <div className="kv-grid__item kv-grid__item--full">
            <dt>Hash SHA-256 (original)</dt>
            <dd className="mono mono--wrap" title={doc?.hash_sha256}>
              {doc?.hash_sha256 ? `${doc.hash_sha256.slice(0, 16)}…${doc.hash_sha256.slice(-8)}` : '—'}
            </dd>
          </div>
        </dl>
      </details>
    </Card>
  );
}
